import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type {
  ApiClient, SanitaerBereichslauf, SanitaerBerechnungsanlagen,
  SanitaerNetzteile, SanitaerNetzteilzeile, SanitaerStrang, SanitaerStraenge,
  SanitaerStrangteilstrecken, SanitaerTeilstrecke,
  SanitaerTeilstreckenposten,
} from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import {
  Baum, ContentSection, DataTable, EmptyState, ErrorState, ExportLeiste, Feld, KpiCard, KpiReihe, LoadingState,
  PropertyGrid, StatusBadge, Vorbehalte, formatiereZahl,
  type BaumKnoten, type ExportZiel, type Spalte, type Vorbehalt, type Zustand,
} from '../muster';
import { Rechenstand, sanitaerFachvorbehalt, zeitpunkt } from './sanitaer/kopf';
import './sanitaer/sanitaer.css';

/**
 * Die Sanitärrechnung nach Strängen, Teilstrecken und Posten.
 *
 * --- Warum es diese Seite gibt -------------------------------------------------
 *
 * Bis Schritt 3c lag das Ergebnis je Strang ausschließlich als JSON in
 * `sanitaer_berechnungszeile.TeilstreckenJson` — 90,1 MB über 30 Läufe, davon
 * 43,9 MB an den Trinkwassersträngen. Die Gattungstabelle auf der
 * Berechnungsseite zeigte Kopfzahlen; wer wissen wollte, WO der Druck abfällt,
 * musste einen 6,78-MB-Blob laden.
 *
 * Seit Migration 0494 steht jeder Posten als Zeile. Diese Seite liest von dort.
 *
 * --- Warum links ein Baum und rechts eine Postenliste ------------------------
 *
 * Dasselbe Muster wie bei der Lüftung (Schritt 1c): Links Bereich → Strang,
 * rechts der gewählte Strang Posten für Posten, mit der laufenden Summe. Eine
 * Endzahl wird geglaubt oder verworfen; erst die laufende Summe zeigt, an
 * welcher Stelle sie entsteht.
 *
 * --- Warum die Herkunft an jeder Zeile steht ----------------------------------
 *
 * Befund S-10: Die Rauigkeit stand am Rohr mit Herkunft „norm" und trug den
 * Wert 0 — die Herkunft behauptete einen Normwert, den es nicht gab. Eine
 * Zahl ohne sichtbare Herkunft ist genau deshalb keine Auskunft.
 *
 * --- Was diese Seite NICHT behauptet ------------------------------------------
 *
 * Ein „Strang" ist hier kein Strang im Sinne des Planers. Die
 * Trinkwasserrechnung schneidet nach (Netzteil, Seite, Etage), und 79,2 % aller
 * Kaltwasserbauteile hängen in EINEM Netzteil (Befund S-11). Solange das Netz
 * nicht gerichtet ist, ist jede Scheibe eine Ersatzgröße — das steht als
 * Vorbehalt über der Liste und nicht in einer Fußnote.
 *
 * --- TX-STANDARD S1 (Paket TS9b1a, TD3) ---------------------------------------
 *
 * `PageHeader` mit dem Rechenstand des gewählten Bereichs (Wahl über den Baum:
 * ein Bereichsknoten oder ein Strang) und der Exportleiste dieses Bereichs
 * (PDF Bericht, Excel aus TS11, PDF Schema über das Register); darunter die
 * `Vorbehalte`: die Fachfragen je Bereich (SAN-05/AUA-22, derselbe Satz wie die
 * Mappe), die Zirkulation, die die Mappe auslässt (SAN-08/30), Zwischenstand
 * (PB44), „veraltet“ (S-21) und der Strang als Ersatzgröße (S-11). Baum über
 * das Muster `Baum` ohne die 40er-Kappung, alle Tabellen über `DataTable`
 * (keine 200er-Kappung mehr, Zeilenwahl über `beiZeileKlick`), Marken über
 * `StatusBadge`, Zahlen über `formatiereZahl`, Zeiten über `zeitpunkt`.
 */

interface Eigenschaften {
  api: ApiClient;
  projektId: string;
}

export const BEREICHSNAMEN: Record<string, string> = {
  trinkwasser: 'Trinkwasser',
  warmwasser: 'Warmwasser',
  schmutzwasser: 'Schmutzwasser',
  fettabwasser: 'Fettabwasser',
  regenwasser: 'Regen- und Notentwässerung',
  abwasser_fall: 'Fallleitungen (DIN EN 12056-2)',
};

const KREISNAMEN: Record<string, string> = {
  trinkwasser_kalt: 'Trinkwasser kalt',
  trinkwasser_warm: 'Trinkwasser warm',
  zirkulation: 'Zirkulation',
  schmutzwasser: 'Schmutzwasser',
  schmutzwasser_grund: 'Schmutzwasser-Grundleitung',
  entlueftung: 'Entlüftung',
  kondensat: 'Kondensat',
  regenwasser: 'Regenwasser',
  notentwaesserung: 'Notentwässerung',
  druckleitung: 'Druckleitung',
  fettabwasser: 'Fettabwasser',
};

const GATTUNGSNAMEN: Record<string, string> = {
  druckerhoehung: 'Druckerhöhung — Gesamtnetz',
  druckzone: 'Druckerhöhung je Druckzone',
  station: 'Frischwasserstationen',
  zirkulation: 'Zirkulation nach DVGW W 553',
  durchlauferhitzer: 'Durchlauferhitzer',
  hebeanlage: 'Hebeanlagen',
  vorbehaelter: 'Vorbehälteranlagen',
  fettabscheider: 'Fettabscheider',
};

/** SAN-08/30 (R5, angenommen von main 26.10.): die Zirkulation steht hier, die Excel-Mappe Warmwasser lässt sie aus. */
export const VORBEHALT_ZIRKULATION = 'SAN-08/30: Die Zirkulation nach DVGW W 553 steht hier aus dem Lauf; die '
  + 'Excel-Mappe Warmwasser lässt sie aus, bis Dämmklassen und Dämmmatrix festgelegt sind (TS8).';

const bereichsname = (b: string | null | undefined) => (b ? BEREICHSNAMEN[b] ?? b : 'ohne Bereich');
const fehlertext = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** Der Bereich des Kopfs ohne eigene Wahl: Trinkwasser, sonst der erste mit Lauf, sonst der erste mit Teilstrecken. */
export function bereichVorgabe(laeufe: SanitaerBereichslauf[], straenge: SanitaerStraenge): string | null {
  if (laeufe.some((l) => l.bereich === 'trinkwasser')) return 'trinkwasser';
  return laeufe[0]?.bereich ?? straenge.jeBereich.find((b) => b.teilstrecken > 0)?.bereich ?? null;
}

/**
 * Die Vorbehalte der Seite in der Reihenfolge des Kastens (S1.2 Nr. 2): je Fachfrage EINE Zeile mit den Bereichen,
 * die unter ihr stehen (R6), die Zirkulation (R5), Zwischenstand (PB44), veraltet (S-21), der Strang als Ersatzgröße
 * (S-11).
 */
export function teilstreckenVorbehalte(
  straenge: SanitaerStraenge, laeufe: SanitaerBereichslauf[], anlagen: SanitaerBerechnungsanlagen | null,
): Vorbehalt[] {
  const aus: Vorbehalt[] = [];
  const bereiche = [...new Set([
    ...laeufe.map((l) => l.bereich),
    ...straenge.jeBereich.filter((b) => b.teilstrecken > 0).map((b) => b.bereich),
  ])];

  const jeFrage = new Map<string, string[]>();
  for (const b of bereiche) {
    const v = sanitaerFachvorbehalt(b);
    if (v) jeFrage.set(v.text, [...(jeFrage.get(v.text) ?? []), bereichsname(b)]);
  }
  for (const [text, namen] of jeFrage) aus.push({ art: 'fachfrage', text: `${namen.join(', ')}: ${text}` });

  if ((anlagen?.gruppen.find((g) => g.gattung === 'zirkulation')?.zeilen.length ?? 0) > 0) {
    aus.push({ art: 'fachfrage', text: VORBEHALT_ZIRKULATION });
  }

  const gesperrt = laeufe.filter((l) => l.bemessungGesperrt);
  if (gesperrt.length > 0) {
    aus.push({
      art: 'sonstiges',
      text: `Zwischenstand, keine Bemessung (doppelte Verknüpfung) in ${gesperrt.map((l) => bereichsname(l.bereich)).join(', ')}`
        + ': die Zahlen sind nicht entdoppelt - als Zwischenstand brauchbar, als Nachweis nach Norm nicht.',
    });
  }

  const veraltet = laeufe.filter((l) => l.standVeraltet);
  if (veraltet.length > 0) {
    const aeltester = veraltet.slice().sort((a, b) => (b.veraltetTage ?? 0) - (a.veraltetTage ?? 0))[0];
    aus.push({
      art: 'veraltet',
      text: `${veraltet.length === 1 ? `Der Bereich ${bereichsname(aeltester.bereich)} ist` : `${veraltet.length} Bereiche sind`}`
        + ' gegen einen älteren Modellstand gerechnet'
        + (typeof aeltester.veraltetTage === 'number' ? ` - bis zu ${formatiereZahl(aeltester.veraltetTage, 1)} Tage` : '')
        + (aeltester.modellstand ? `; Sanitäraufnahme vom ${zeitpunkt(aeltester.modellstand)}` : '')
        + '. Jede Zahl auf dieser Seite gilt für einen Modellstand, den es nicht mehr gibt; über „Alle Bereiche '
        + 'rechnen“ auf der Bestandsseite entsteht der neue.',
    });
  }

  const ohneDruck = straenge.jeBereich
    .filter((b) => b.bereich !== 'trinkwasser' && b.bereich !== 'warmwasser' && b.teilstrecken > 0)
    .map((b) => bereichsname(b.bereich));
  aus.push({
    art: 'sonstiges',
    text: 'Ein „Strang“ ist hier eine Ersatzgröße: Die Trinkwasserrechnung schneidet nach (Netzteil, Seite, Etage); '
      + '79,2 % aller Kaltwasserbauteile hängen in EINEM Netzteil, und jede Etagenscheibe bekommt den Durchgangsstrom '
      + 'des ganzen Netzteils (Befund S-11).'
      + (ohneDruck.length > 0
        ? ` In ${ohneDruck.join(', ')} steht kein Druckverlust - die Entwässerung rechnet im Freispiegel (DIN EN 12056), `
          + 'maßgebend sind Füllungsgrad, Gefälle und Fließgeschwindigkeit.'
        : ''),
  });
  return aus;
}

interface Daten {
  straenge: SanitaerStraenge;
  netzteile: SanitaerNetzteile;
  anlagen: SanitaerBerechnungsanlagen;
  laeufe: SanitaerBereichslauf[];
}

export function SanitaerTeilstrecken({ api, projektId }: Eigenschaften) {
  const [daten, setDaten] = useState<Daten | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [versuch, setVersuch] = useState(0);

  const [bereich, setBereich] = useState<string | null>(null);
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [posten, setPosten] = useState<SanitaerStrangteilstrecken | null>(null);
  const [postenFehler, setPostenFehler] = useState<string | null>(null);
  const [postenVersuch, setPostenVersuch] = useState(0);
  const [postenId, setPostenId] = useState<string | null>(null);
  const [einzel, setEinzel] = useState<SanitaerTeilstreckenposten | null>(null);
  const [einzelFehler, setEinzelFehler] = useState<string | null>(null);

  const [suche, setSuche] = useState('');
  const [nurBeanstandet, setNurBeanstandet] = useState(false);

  useEffect(() => {
    setDaten(null);
    setFehler(null);
    Promise.all([
      api.sanitaerStraenge(projektId),
      api.sanitaerNetzteile(projektId),
      api.sanitaerBerechnungsanlagen(projektId),
      api.sanitaerBerechnungslaeufe(projektId),
    ])
      .then(([s, n, a, l]) => setDaten({ straenge: s, netzteile: n, anlagen: a, laeufe: l ?? [] }))
      .catch((e) => setFehler(fehlertext(e)));
  }, [api, projektId, versuch]);

  useEffect(() => {
    setPosten(null);
    setPostenFehler(null);
    if (!gewaehlt) return;
    api.sanitaerStrangteilstrecken(projektId, gewaehlt)
      .then(setPosten)
      .catch((e) => setPostenFehler(fehlertext(e)));
  }, [api, projektId, gewaehlt, postenVersuch]);

  useEffect(() => {
    setEinzel(null);
    setEinzelFehler(null);
    if (!postenId) return;
    api.sanitaerTeilstreckenposten(projektId, postenId)
      .then(setEinzel)
      .catch((e) => setEinzelFehler(fehlertext(e)));
  }, [api, projektId, postenId]);

  const gefiltert = useMemo(() => {
    if (!daten) return [];
    const s = suche.trim().toLowerCase();
    return daten.straenge.straenge.filter((x) =>
      (!nurBeanstandet || x.beanstandet > 0)
      && (s === ''
        || (x.bezeichnung ?? '').toLowerCase().includes(s)
        || (x.geschoss ?? '').toLowerCase().includes(s)
        || x.kreise.some((k) => k.toLowerCase().includes(s))));
  }, [daten, suche, nurBeanstandet]);

  const aktiverBereich = daten ? bereich ?? bereichVorgabe(daten.laeufe, daten.straenge) : null;
  const lauf = daten?.laeufe.find((l) => l.bereich === aktiverBereich) ?? null;

  const ohneLauf = !lauf ? { grund: 'Für diesen Bereich liegt noch keine Berechnung vor.' } : undefined;
  const ziele: ExportZiel[] = aktiverBereich ? [
    {
      art: 'pdf', beschriftung: 'PDF Bericht', gesperrt: ohneLauf,
      laden: () => api.sanitaerBerichtPdf(projektId, aktiverBereich),
    },
    {
      art: 'excel', beschriftung: 'Excel', gesperrt: ohneLauf,
      laden: () => api.sanitaerBerechnungExcel(projektId, aktiverBereich),
    },
    {
      // TS10 (AG-F2): über das Register - kein Sichtstand mehr.
      art: 'pdf', beschriftung: 'PDF Schema', gesperrt: ohneLauf,
      laden: () => api.sanitaerSchemaPdf(projektId, aktiverBereich),
    },
  ] : [];

  const kopf = (
    <PageHeader
      titel="Teilstrecken"
      beschreibung={'Die Sanitärrechnung nach Bereich, Strang und Posten - Rohr für Rohr und Formstück für Formstück, '
        + 'mit der Herkunft jedes Werts.'}
      meta={daten && aktiverBereich ? (
        <span data-kopfbereich={aktiverBereich}>
          {bereichsname(aktiverBereich)}:{' '}
          <Rechenstand
            lauf={lauf ? { id: lauf.id, norm: lauf.norm, gerechnetAm: lauf.gerechnetAm, modellstand: lauf.modellstand } : null}
            veraltet={lauf?.standVeraltet}
          />
        </span>
      ) : undefined}
      aktionen={ziele.length > 0 ? <ExportLeiste ziele={ziele} /> : undefined}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState
          titel="Die Teilstrecken konnten nicht geladen werden."
          meldung="Stränge, Netzteile, Anlagen und Läufe kommen in einem Abruf; ohne sie zeigt die Seite nichts."
          technisch={fehler}
          beiErneutVersuchen={() => setVersuch((v) => v + 1)}
        />
      </>
    );
  }
  if (!daten) return <>{kopf}<LoadingState text="Teilstrecken abrufen …" zeilen={8} spalten={4} /></>;

  const waehleStrang = (s: SanitaerStrang) => {
    setGewaehlt(s.schluessel);
    setPostenId(null);
    if (s.bereich) setBereich(s.bereich);
  };

  return (
    <>
      {kopf}
      <Vorbehalte eintraege={teilstreckenVorbehalte(daten.straenge, daten.laeufe, daten.anlagen)} />

      <Kennwerte straenge={daten.straenge} netzteile={daten.netzteile} anlagen={daten.anlagen} laeufe={daten.laeufe} />

      <ContentSection
        titel="Stränge und Posten"
        beschreibung="Links Bereich → Strang (ein Bereich wählt den Rechenstand im Kopf), rechts der gewählte Strang Posten für Posten."
      >
        <div className="san-zweispalter">
          <Strangbaum
            straenge={gefiltert}
            gesamt={daten.straenge.straenge.length}
            jeBereich={daten.straenge.jeBereich}
            gewaehlt={gewaehlt}
            bereich={aktiverBereich}
            waehleStrang={waehleStrang}
            waehleBereich={(b) => setBereich(b)}
            suche={suche} setSuche={setSuche}
            nurBeanstandet={nurBeanstandet} setNurBeanstandet={setNurBeanstandet}
          />

          <div>
            {!gewaehlt && (
              <EmptyState
                titel="Kein Strang gewählt."
                erklaerung={'Links einen Bereich aufklappen und einen Strang wählen. Rechts steht dann seine '
                  + 'Postenliste - Rohr für Rohr und Formstück für Formstück, mit Δp_R, Δp_E und der laufenden Summe.'}
              />
            )}
            {gewaehlt && postenFehler && (
              <ErrorState
                titel="Die Posten dieses Strangs konnten nicht geladen werden."
                technisch={postenFehler}
                beiErneutVersuchen={() => setPostenVersuch((v) => v + 1)}
              />
            )}
            {gewaehlt && !posten && !postenFehler && <LoadingState text="Posten abrufen …" zeilen={8} spalten={6} />}
            {posten && !postenId && <Postenliste key={gewaehlt ?? ''} daten={posten} waehlePosten={setPostenId} />}
            {postenId && (
              <Postendetail key={postenId} daten={einzel} fehler={einzelFehler} zurueck={() => setPostenId(null)} />
            )}
          </div>
        </div>
      </ContentSection>

      <Netzteilliste daten={daten.netzteile} />
      <Anlagenliste daten={daten.anlagen} />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Die Kennwerte
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ein Kennzahlenwert als Zahl - oder null.
 *
 * Die Kennzahlen kommen untypisiert aus dem Lauf: Je Bereich sind es andere,
 * und manche sind Texte ("hotel" als Gebäudeart). Ein Text in einer
 * Zahlenkarte stünde dort als „NaN".
 */
export function z(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** Eine bewertete Karte bekommt Zustand und Text nur, wenn die Grenze verletzt ist (S1.2 Nr. 3). */
const bewertet = (verletzt: boolean, text: string): { zustand?: Zustand; zustandText?: string } =>
  (verletzt ? { zustand: 'warnung', zustandText: text } : {});

/**
 * Die Kopfzahlen der Rechnung.
 *
 * Die Förderhöhe steht MIT dem Vermerk, dass sie eine untere Schranke ist,
 * solange Formstücke ohne Beiwertregel im Weg stehen: Deren Anteil geht mit
 * 0 Pa ein, und die Summe ist dann kein Nachweis, sondern eine untere Grenze.
 */
function Kennwerte({ straenge, netzteile, anlagen, laeufe }: {
  straenge: SanitaerStraenge;
  netzteile: SanitaerNetzteile;
  anlagen: SanitaerBerechnungsanlagen;
  laeufe: SanitaerBereichslauf[];
}) {
  const dea = anlagen.gruppen.find((g) => g.gattung === 'druckerhoehung')?.zeilen[0];
  const werte = (dea?.werte ?? {}) as Record<string, unknown>;
  const zirkulation = anlagen.gruppen.find((g) => g.gattung === 'zirkulation');
  const k = (b: string) => (laeufe.find((l) => l.bereich === b)?.kennzahlen ?? {}) as Record<string, unknown>;
  const tw = k('trinkwasser');
  const ww = k('warmwasser');
  const rw = k('regenwasser');
  const ohneWarmnetz = z(ww.OhneWarmnetz) ?? 0;
  const ohneZetaWeg = z(werte.WegOhneZetaregel) ?? 0;

  return (
    <ContentSection
      titel="Kennwerte der Rechnung"
      meta={`Stand der Teilstrecken: ${straenge.stand ? zeitpunkt(straenge.stand) : 'noch kein Lauf mit Teilstreckenablage'}`}
    >
      {/* Die Bemessungsgrößen zuerst - das, was der Fachplaner nachrechnet; die Zahl der Stränge sagt nur, wie fein
          die Rechnung zerlegt hat. */}
      <KpiReihe>
        <KpiCard titel="Spitzendurchfluss kalt (DIN 1988-300)" wert={z(tw.SpitzeKaltLs)} einheit="l/s" nachkomma={2} />
        <KpiCard titel="Spitzendurchfluss warm" wert={z(ww.SpitzeWarmLs)} einheit="l/s" nachkomma={2} />
        <KpiCard
          titel="warm als OBERE Schranke - mit den Zapfstellen ohne Netz" wert={z(ww.SpitzeWarmObereSchrankeLs)}
          einheit="l/s" nachkomma={2} {...bewertet(ohneWarmnetz > 0, 'obere Schranke')}
        />
        <KpiCard
          titel="Warmwasserzapfstellen OHNE Netz" wert={z(ww.OhneWarmnetz)}
          vergleich={z(ww.OhneWarmnetzLs) === null ? undefined : `${formatiereZahl(z(ww.OhneWarmnetzLs), 2)} l/s`}
          {...bewertet(ohneWarmnetz > 0, 'ohne Netz')}
        />
        <KpiCard
          titel="Deckung der Notentwässerung (r(5,100))" wert={z(rw.DeckungNotProzent)} einheit="%" nachkomma={1}
          {...bewertet((z(rw.DeckungNotProzent) ?? 100) < 100, 'unter 100 %')}
        />
        <KpiCard
          titel="Deckung der Regelentwässerung (r(5,5))" wert={z(rw.DeckungRegelProzent)} einheit="%" nachkomma={1}
          {...bewertet((z(rw.DeckungRegelProzent) ?? 100) < 100, 'unter 100 %')}
        />
      </KpiReihe>

      <KpiReihe>
        <KpiCard titel="Stränge" wert={straenge.summe.straenge} />
        <KpiCard titel="Teilstrecken" wert={straenge.summe.teilstrecken} />
        <KpiCard
          titel="beanstandete Netzteile" wert={netzteile.summe.beanstandet}
          {...bewertet(netzteile.summe.beanstandet > 0, 'beanstandet')}
        />
        <KpiCard
          titel="Formstücke ohne Beiwert" wert={straenge.summe.ohneZetaregel}
          {...bewertet(straenge.summe.ohneZetaregel > 0, 'Anteil mit 0 Pa')}
        />
      </KpiReihe>

      {dea && (
        <>
          <h3>Druckerhöhung</h3>
          <KpiReihe>
            <KpiCard titel="Förderhöhe erforderlich" wert={z(werte.ErforderlichM)} einheit="m" nachkomma={2} />
            <KpiCard titel="davon Reibung Δp_R" wert={z(werte.ReibungM)} einheit="m" nachkomma={2} />
            <KpiCard titel="davon Einzelwiderstand Δp_E" wert={z(werte.EinzelwiderstandM)} einheit="m" nachkomma={2} />
            <KpiCard
              titel="Fehlbetrag gegen den Anschlussdruck" wert={z(werte.FehlbetragM)} einheit="m" nachkomma={2}
              {...bewertet((z(werte.FehlbetragM) ?? 0) > 0, 'Fehlbetrag')}
            />
          </KpiReihe>
          {/* Die Schranke AUSDRÜCKLICH und nicht als Fußnote: Solange Formstücke ohne Regel im Weg stehen, ist Δp_E zu
              klein - und damit die Förderhöhe eine UNTERE Schranke. Wer das verschweigt, lässt eine Pumpe zu klein
              wählen. */}
          {ohneZetaWeg > 0 ? (
            <p className="schwach" data-dea-schranke>
              <StatusBadge zustand="warnung" text="untere Schranke" klein />{' '}
              {formatiereZahl(ohneZetaWeg, 0)} der {formatiereZahl(z(werte.WegFormstuecke), 0)} Formstücke auf dem
              maßgebenden Weg haben KEINE Beiwertregel. Ihr Anteil geht mit 0 Pa ein - die genannte Förderhöhe ist
              damit eine UNTERE Schranke und kein Nachweis.
            </p>
          ) : (
            <p className="schwach" data-dea-schranke>
              Alle {formatiereZahl(z(werte.WegFormstuecke), 0)} Formstücke des maßgebenden Weges tragen einen Beiwert
              aus <code>sanitaer_zetaregeln</code>. Δp = Δp_R + Δp_E + Δp_Ap nach DIN 1988-300 Abschnitt 6.3; der
              Armaturenzuschlag Δp_Ap steht getrennt mit {formatiereZahl(z(werte.ArmaturenM), 2)} m.
            </p>
          )}
        </>
      )}

      {zirkulation && zirkulation.zeilen.length > 0 && (
        <>
          <h3>Zirkulation nach DVGW W 553</h3>
          <KpiReihe>
            <KpiCard titel="Kreise" wert={zirkulation.zeilen.length} />
            <KpiCard titel="Summe Kennwert" wert={zirkulation.zeilen.reduce((s, x) => s + (x.kennwert ?? 0), 0)} nachkomma={2} />
          </KpiReihe>
        </>
      )}
    </ContentSection>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Der Baum
// ═══════════════════════════════════════════════════════════════════════════

type Baumdaten = { art: 'bereich'; bereich: string } | { art: 'strang'; strang: SanitaerStrang };

/**
 * Links: Bereich → Strang über das Muster `Baum` - ohne die frühere Kappung auf 40 Stränge je Bereich (S1.3); der
 * Baum rollt in seinem Bereich und zeichnet ab vielen Zeilen nur die sichtbaren. Die beanstandeten stehen vorn, weil
 * die Antwort schon so sortiert ist.
 */
function Strangbaum({
  straenge, gesamt, jeBereich, gewaehlt, bereich, waehleStrang, waehleBereich,
  suche, setSuche, nurBeanstandet, setNurBeanstandet,
}: {
  straenge: SanitaerStrang[];
  gesamt: number;
  jeBereich: SanitaerStraenge['jeBereich'];
  gewaehlt: string | null;
  bereich: string | null;
  waehleStrang: (s: SanitaerStrang) => void;
  waehleBereich: (b: string) => void;
  suche: string;
  setSuche: (s: string) => void;
  nurBeanstandet: boolean;
  setNurBeanstandet: (b: boolean) => void;
}) {
  const knoten: BaumKnoten<Baumdaten>[] = useMemo(() => {
    const karte = new Map<string, SanitaerStrang[]>();
    for (const s of straenge) {
      const b = s.bereich ?? 'ohne Bereich';
      karte.set(b, [...(karte.get(b) ?? []), s]);
    }
    return [...karte.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], 'de'))
      .map(([b, liste]) => ({
        id: `b:${b}`,
        titel: bereichsname(b),
        anzahl: liste.length,
        daten: { art: 'bereich' as const, bereich: b },
        wert: `${formatiereZahl(jeBereich.find((x) => x.bereich === b)?.teilstrecken ?? 0, 0)} TS`,
        marke: b === bereich ? <StatusBadge zustand="info" text="im Kopf" klein /> : undefined,
        kinder: liste.map((s) => ({
          id: s.schluessel,
          titel: s.bezeichnung ?? s.schluessel,
          hinweis: s.geschoss ?? undefined,
          daten: { art: 'strang' as const, strang: s },
          wert: s.druckverlustPa > 0 ? `${formatiereZahl(s.druckverlustPa, 0)} Pa` : `${formatiereZahl(s.teilstrecken, 0)} TS`,
          marke: (s.istMassgebend || s.beanstandet > 0) ? (
            <>
              {s.istMassgebend && <StatusBadge zustand="info" text="maßgebend" klein />}
              {s.beanstandet > 0 && <StatusBadge zustand="warnung" text={`${formatiereZahl(s.beanstandet, 0)} beanstandet`} klein />}
            </>
          ) : undefined,
        })),
      }));
  }, [straenge, jeBereich, bereich]);

  return (
    <div>
      <Feld art="suche" beschriftung="Strang suchen" wert={suche} platzhalter="Strang, Kreis oder Geschoss"
        beiAenderung={(w) => setSuche((w as string) ?? '')} />
      <Feld art="haken" beschriftung="nur Stränge mit Beanstandung" wert={nurBeanstandet}
        beiAenderung={(w) => setNurBeanstandet(Boolean(w))} />
      <p className="schwach" data-strangzahl>{formatiereZahl(straenge.length, 0)} von {formatiereZahl(gesamt, 0)} Strängen</p>
      <Baum<Baumdaten>
        name="Stränge je Bereich"
        knoten={knoten}
        gewaehlt={gewaehlt}
        anfangsOffen={['b:trinkwasser']}
        beiWahl={(k) => {
          if (k.daten?.art === 'strang') waehleStrang(k.daten.strang);
          else if (k.daten?.art === 'bereich') waehleBereich(k.daten.bereich);
        }}
        hoehe={560}
        leer={(
          <EmptyState klein
            titel={gesamt === 0 ? 'Noch kein Lauf mit Teilstreckenablage.' : 'Kein Strang passt zur Suche.'}
            erklaerung={gesamt === 0
              ? 'Teilstrecken entstehen mit der Sanitärrechnung („Alle Bereiche rechnen“ auf der Bestandsseite).'
              : 'Suche oder „nur Stränge mit Beanstandung“ zurücknehmen.'} />
        )}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Die Postenliste
// ═══════════════════════════════════════════════════════════════════════════

/** Klasse einer früheren Marke → Zustand der Statusmarke (gut / warnung / schlecht → fehler). */
type Markenart = 'gut' | 'warnung' | 'fehler';
const marke = (zustand: Markenart, text: string, titel?: string, key?: string): ReactNode =>
  <StatusBadge key={key ?? text} zustand={zustand} text={text} titel={titel} klein />;

/**
 * Die Fließrichtung an der Teilstrecke — und woher ihr Durchfluss stammt.
 *
 * Die zweite Marke ist die wichtigere. `obergrenze` heißt: Der Durchfluss ist
 * auf das gedeckelt, was das Rohr bei 2 m/s fasst. Die Geschwindigkeit daneben
 * KANN ihre eigene Grenze dann nicht überschreiten (Befund S-19). `kumuliert`
 * heißt: aus den stromabwärts liegenden Vollströmen nach DIN 1988-300.
 */
export function richtungsmarken(t: SanitaerTeilstrecke): ReactNode[] {
  const m: ReactNode[] = [];
  if (t.richtungHerkunft === 'quelle_baum') {
    m.push(marke('gut', t.richtungUmgekehrt ? '◄ Baum' : '► Baum', t.richtungUmgekehrt
      ? 'Fließt GEGEN die Zeichenrichtung des Rohres (Nach → Von)' : 'Fließt in Zeichenrichtung (Von → Nach)', 'ri'));
  } else if (t.richtungHerkunft === 'gefaelle') {
    m.push(marke('warnung', t.richtungUmgekehrt ? '◄ Gefälle' : '► Gefälle',
      'Richtung aus der Schwerkraft und nicht aus dem Baum', 'ri'));
  } else if (t.richtungHerkunft) {
    m.push(marke('fehler', 'Richtung offen', 'Fließrichtung nicht bestimmbar', 'ri'));
  }

  if (t.durchflussHerkunft === 'kumuliert') {
    m.push(marke('gut', 'V̇ kumuliert', 'Durchfluss aus den stromabwärts liegenden Vollströmen', 'q'));
  } else if (t.durchflussHerkunft === 'obergrenze') {
    m.push(marke('fehler', 'V̇ Obergrenze', 'Durchfluss gedeckelt auf das, was das Rohr bei 2 m/s fasst - die '
      + 'Geschwindigkeit daneben ist eine Schranke und kein Messwert (Befund S-19).', 'q'));
  } else if (t.durchflussHerkunft === 'netzteil') {
    m.push(marke('warnung', 'V̇ Netzteil', 'Abfluss des ganzen Netzteils - für die Äste zu viel', 'q'));
  } else if (t.durchflussHerkunft === 'vorrohr_kumuliert') {
    // Formstück oder Armatur: kein eigener Durchfluss, die Geschwindigkeit ist die des zuletzt durchlaufenen Rohres.
    m.push(marke('warnung', 'v aus Vorrohr', 'Kein eigener Durchfluss - die Geschwindigkeit ist die des zuletzt '
      + 'durchlaufenen Rohres, dort kumuliert.', 'q'));
  } else if (t.durchflussHerkunft === 'vorrohr_obergrenze') {
    m.push(marke('fehler', 'v aus Vorrohr (Schranke)', 'Kein eigener Durchfluss - die Geschwindigkeit ist die des '
      + 'zuletzt durchlaufenen Rohres, und die war dort auf 2 m/s gedeckelt (S-19).', 'q'));
  }
  return m;
}

/**
 * Woher die Eingangswerte dieses Postens stammen: `gut` = aus einem gepflegten
 * Katalog, `warnung` = Rückfall, `fehler` = keine Regel - dieselben drei
 * Zustände wie bei der Lüftung.
 */
export function herkunftsmarken(t: SanitaerTeilstrecke): ReactNode[] {
  const m: ReactNode[] = [];
  if (t.rauigkeitHerkunft) {
    m.push(marke(t.rauigkeitHerkunft === 'werkstoff' ? 'gut' : 'warnung', `k ${t.rauigkeitHerkunft}`,
      `Rauigkeit ${formatiereZahl(t.rauigkeitMm)} mm, Herkunft ${t.rauigkeitHerkunft}`, 'rau'));
  }
  if (t.zetaHerkunft === 'bauform') m.push(marke('gut', 'ζ Bauform', t.zetaQuelle ?? undefined, 'zeta'));
  else if (t.zetaHerkunft) m.push(marke('fehler', `ζ ${t.zetaHerkunft}`, t.zetaQuelle ?? undefined, 'zeta'));
  return m;
}

const marken = (m: ReactNode[]) => (m.length === 0 ? '—' : <span className="san-marken">{m}</span>);

/**
 * Rechts: die Posten des gewählten Strangs in der Reihenfolge der Rechnung - VOLLSTÄNDIG (früher ab 200 gekappt).
 * Die Reihenfolge ist die der RECHNUNG und nicht die des größten Beitrags: Die laufende Summe ergibt nur in ihr einen
 * Sinn. Eine Zeile öffnet den Posten im Einzelnen.
 */
function Postenliste({ daten, waehlePosten }: {
  daten: SanitaerStrangteilstrecken;
  waehlePosten: (id: string) => void;
}) {
  const s = daten.strang;
  const mitDruck = s.summePa > 0;

  const gemeinsam: Spalte<SanitaerTeilstrecke>[] = [
    { schluessel: 'nummer', titel: 'Nr', art: 'zahl', wert: (t) => t.nummer },
    { schluessel: 'art', titel: 'Art', wert: (t) => t.art },
    { schluessel: 'bauteil', titel: 'Bauteil', wert: (t) => t.bauform ?? t.typ ?? t.familie ?? null },
    { schluessel: 'nennweite', titel: 'DN', art: 'zahl', wert: (t) => t.nennweite },
    { schluessel: 'laengeM', titel: 'Länge', art: 'zahl', einheit: 'm', nachkomma: 2, wert: (t) => t.laengeM || null },
    {
      schluessel: 'herkunft', titel: 'Herkunft', sortierbar: false,
      wert: (t) => [t.rauigkeitHerkunft, t.zetaHerkunft].filter(Boolean).join(' '),
      inhalt: (t) => marken(herkunftsmarken(t)),
    },
    {
      schluessel: 'richtung', titel: 'Richtung', sortierbar: false,
      wert: (t) => [t.richtungHerkunft, t.durchflussHerkunft].filter(Boolean).join(' '),
      inhalt: (t) => marken(richtungsmarken(t)),
    },
  ];
  const druck: Spalte<SanitaerTeilstrecke>[] = [
    { schluessel: 'volumenstrom', titel: 'V̇', art: 'zahl', einheit: 'l/s', nachkomma: 3, wert: (t) => t.volumenstrom },
    { schluessel: 'geschwindigkeit', titel: 'v', art: 'zahl', einheit: 'm/s', nachkomma: 2, wert: (t) => t.geschwindigkeit },
    { schluessel: 'zeta', titel: 'ζ', art: 'zahl', nachkomma: 2, wert: (t) => t.zeta },
    { schluessel: 'reibungPa', titel: 'Δp_R', art: 'zahl', einheit: 'Pa', nachkomma: 1, wert: (t) => t.reibungPa, fuss: 'summe' },
    { schluessel: 'einzelPa', titel: 'Δp_E', art: 'zahl', einheit: 'Pa', nachkomma: 1, wert: (t) => t.einzelPa, fuss: 'summe' },
    { schluessel: 'summeDruckverlustM', titel: 'Σ', art: 'zahl', einheit: 'm', nachkomma: 3, wert: (t) => t.summeDruckverlustM },
  ];
  const freispiegel: Spalte<SanitaerTeilstrecke>[] = [
    { schluessel: 'abfluss', titel: 'Q', art: 'zahl', einheit: 'l/s', nachkomma: 3, wert: (t) => t.abfluss },
    { schluessel: 'geschwindigkeit', titel: 'v', art: 'zahl', einheit: 'm/s', nachkomma: 2, wert: (t) => t.geschwindigkeit },
    // TS10 (AG-F4): in Prozent wie PDF und Mappe - der Kern speichert das Verhältnis (0,01 = 1 %).
    { schluessel: 'gefaelle', titel: 'Gefälle', art: 'zahl', einheit: '%', nachkomma: 2, wert: (t) => (t.gefaelle == null ? null : t.gefaelle * 100) },
    { schluessel: 'fuellungsgrad', titel: 'h/d', art: 'zahl', nachkomma: 3, wert: (t) => t.fuellungsgrad },
    // Q_max und die erforderliche Nennweite an JEDER Abwasserzeile: Eine Bemessung, die ihre Zahl nur im Fehlerfall
    // nennt, lässt sich nicht nachrechnen. An einer Sammelleitung bleiben sie leer - dort bemisst die Teilfüllung.
    { schluessel: 'abflussvermoegenLs', titel: 'Q_max', art: 'zahl', einheit: 'l/s', nachkomma: 2, wert: (t) => t.abflussvermoegenLs },
    { schluessel: 'erforderlichDn', titel: 'erf. DN', art: 'zahl', wert: (t) => t.erforderlichDn },
    {
      schluessel: 'lueftung', titel: 'Lüftung', art: 'status', wert: (t) => t.lueftungsart,
      inhalt: (t) => (t.lueftungsart
        ? (t.lueftungsartHerkunft === 'nachgewiesen'
          ? marke('gut', t.lueftungsart, 'Lüftung am Modell nachgewiesen')
          : marke('warnung', `${t.lueftungsart} (nicht nachgewiesen)`,
            'Bemessen mit dem günstigsten Fall der Tabelle (S-04)'))
        : '—'),
    },
    { schluessel: 'leitungsart', titel: 'Leitungsart', wert: (t) => t.leitungsart },
    {
      schluessel: 'bewertung', titel: 'Bewertung', art: 'status', wert: (t) => t.bewertung ?? 'ok',
      inhalt: (t) => (t.bewertung && t.bewertung !== 'ok'
        ? marke('warnung', t.bewertung, t.hinweis ?? undefined)
        : marke('gut', 'ok')),
    },
  ];
  const spalten = [...gemeinsam, ...(mitDruck ? druck : freispiegel),
    { schluessel: 'raumnummer', titel: 'Raum', wert: (t: SanitaerTeilstrecke) => t.raumnummer } as Spalte<SanitaerTeilstrecke>];

  return (
    <ContentSection
      titel={s.bezeichnung ?? s.schluessel}
      meta={s.istMassgebend ? <StatusBadge zustand="info" text="maßgebend" klein /> : undefined}
    >
      <KpiReihe>
        <KpiCard titel="Posten" wert={s.teilstrecken} />
        <KpiCard titel="Rohre / Formstücke" wert={`${formatiereZahl(s.rohre, 0)} / ${formatiereZahl(s.formstuecke, 0)}`} />
        <KpiCard titel="Länge" wert={s.laengeM} einheit="m" nachkomma={2} />
        {mitDruck && (
          <>
            <KpiCard titel="Δp_R Reibung" wert={s.reibungPa} einheit="Pa" nachkomma={0} />
            <KpiCard titel="Δp_E Einzelwiderstand" wert={s.einzelPa} einheit="Pa" nachkomma={0} />
            <KpiCard titel="Δp gesamt" wert={s.summePa} einheit="Pa" nachkomma={0} />
          </>
        )}
      </KpiReihe>

      <p className="schwach">
        Kreise: {s.kreise.map((x) => `${KREISNAMEN[x.kreis] ?? x.kreis} (${formatiereZahl(x.anzahl, 0)})`).join(', ') || '—'}
        {' · '}Rauigkeit: {s.rauigkeit.map((r) => `${r.herkunft} (${formatiereZahl(r.anzahl, 0)})`).join(', ') || 'keine Angabe'}
        {s.ohneZetaregel > 0 && (
          <>
            {' · '}
            <StatusBadge zustand="warnung" klein
              text={`${formatiereZahl(s.ohneZetaregel, 0)} Formstücke ohne Beiwertregel - ihr Anteil geht mit 0 Pa ein`} />
          </>
        )}
      </p>

      <DataTable
        name="Posten"
        beschreibung="In der Reihenfolge der Rechnung; eine Zeile öffnet den Posten im Einzelnen."
        spalten={spalten}
        zeilen={daten.teilstrecken}
        schluessel={(t) => t.id}
        beiZeileKlick={(t) => waehlePosten(t.id)}
        dicht
        ausfuhr
        ausfuhrDateiname={`sanitaer-posten-${s.schluessel}`}
        leer={<EmptyState klein titel="Dieser Strang hat keinen Posten." erklaerung="Die Rechnung hat ihn ohne Teilstrecke abgelegt." />}
      />
    </ContentSection>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Der einzelne Posten
// ═══════════════════════════════════════════════════════════════════════════

type Herkunftzeile = SanitaerTeilstreckenposten['herkuenfte'][number];
type Nachbar = SanitaerTeilstreckenposten['nachbarn'][number];

/**
 * Ein Posten mit allen Herkünften und seinen beiden Nachbarn.
 *
 * Die Nachbarn stehen dabei, weil ein Sprung der Geschwindigkeit erst im
 * Vergleich sichtbar wird: Eine DN 25 zwischen zwei DN 80 ist die Engstelle,
 * und an der Einzelzeile ist das nicht zu sehen.
 */
function Postendetail({ daten, fehler, zurueck }: {
  daten: SanitaerTeilstreckenposten | null;
  fehler: string | null;
  zurueck: () => void;
}) {
  const zurueckKnopf = <button type="button" className="m-knopf" onClick={zurueck}>zurück zur Postenliste</button>;
  if (fehler) {
    return <ErrorState titel="Der Posten konnte nicht geladen werden." technisch={fehler} aktionen={zurueckKnopf} />;
  }
  if (!daten) return <LoadingState text="Posten abrufen …" zeilen={6} spalten={3} />;

  const p = daten.posten;
  const herkunftSpalten: Spalte<Herkunftzeile>[] = [
    { schluessel: 'groesse', titel: 'Größe', immerSichtbar: true, wert: (h) => h.groesse },
    { schluessel: 'wert', titel: 'Wert', art: 'zahl', nachkomma: 4, wert: (h) => h.wert },
    {
      schluessel: 'herkunft', titel: 'Herkunft', art: 'status', wert: (h) => h.herkunft,
      inhalt: (h) => (h.herkunft === 'unbekannt'
        ? marke('warnung', 'unbekannt')
        : h.herkunft === 'trifft nicht zu'
          ? <StatusBadge zustand="neutral" text="trifft nicht zu" klein />
          : marke('gut', h.herkunft)),
    },
    { schluessel: 'quelle', titel: 'Quelle', wert: (h) => h.quelle ?? null },
  ];
  const nachbarSpalten: Spalte<Nachbar>[] = [
    { schluessel: 'nummer', titel: 'Nr', art: 'zahl', wert: (n) => n.nummer },
    { schluessel: 'art', titel: 'Art', wert: (n) => n.art },
    { schluessel: 'typ', titel: 'Typ', wert: (n) => n.typ },
    { schluessel: 'nennweite', titel: 'DN', art: 'zahl', wert: (n) => n.nennweite },
    { schluessel: 'geschwindigkeit', titel: 'v', art: 'zahl', einheit: 'm/s', nachkomma: 2, wert: (n) => n.geschwindigkeit },
    { schluessel: 'druckverlustPa', titel: 'Δp', art: 'zahl', einheit: 'Pa', nachkomma: 1, wert: (n) => n.druckverlustPa },
    { schluessel: 'summeDruckverlustPa', titel: 'Σ', art: 'zahl', einheit: 'Pa', nachkomma: 0, wert: (n) => n.summeDruckverlustPa },
  ];
  const bauteil = Object.entries(daten.bauteil ?? {}).filter(([, v]) => v !== null && typeof v !== 'object');

  return (
    <ContentSection
      titel={`Posten ${p.nummer ?? '—'} — ${p.bauform ?? p.typ ?? p.art ?? ''}`}
      beschreibung={`${p.strang ?? '—'} · ${p.kennzeichen}`}
      aktionen={zurueckKnopf}
    >
      <h3>Herkunft der Eingangswerte</h3>
      <DataTable name="Herkunft der Eingangswerte" spalten={herkunftSpalten} zeilen={daten.herkuenfte}
        schluessel={(h) => h.groesse} dicht suche={false} spaltenwahl={false}
        leer={<EmptyState klein titel="Keine Eingangswerte abgelegt." />} />

      <h3>Nachbarn im Strang</h3>
      <DataTable name="Nachbarn im Strang" spalten={nachbarSpalten} zeilen={daten.nachbarn}
        schluessel={(n) => n.id} aktiveZeile={p.id} dicht suche={false} spaltenwahl={false}
        leer={<EmptyState klein titel="Keine Nachbarn." erklaerung="Der Posten steht allein im Strang." />} />

      {bauteil.length > 0 && (
        <>
          <h3>Das Bauteil im Bestand</h3>
          <PropertyGrid dicht spalten={2} eintraege={bauteil.map(([k, v]) => ({ beschriftung: k, wert: String(v) }))} />
        </>
      )}
    </ContentSection>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Netzteile und Anlagen
// ═══════════════════════════════════════════════════════════════════════════

type Kreiszeile = SanitaerNetzteile['jeKreis'][number];

const bewertungsmarke = (b: string) => (b === 'beanstandet'
  ? marke('fehler', 'beanstandet')
  : b === 'hinweis' ? <StatusBadge zustand="info" text="Hinweis" klein /> : marke('gut', 'ok'));

const zeilenSpalten = (mitBereich: boolean): Spalte<SanitaerNetzteilzeile>[] => [
  {
    schluessel: 'bezeichnung', titel: 'Bezeichnung', immerSichtbar: true, wert: (x) => x.bezeichnung,
    inhalt: (x) => <>{x.istMassgebend && <StatusBadge zustand="info" text="maßgebend" klein />} {x.bezeichnung}</>,
  },
  ...(mitBereich
    ? [{ schluessel: 'bereich', titel: 'Bereich', wert: (x: SanitaerNetzteilzeile) => bereichsname(x.bereich) }]
    : [{ schluessel: 'raumnummer', titel: 'Raum', wert: (x: SanitaerNetzteilzeile) => x.raumnummer }]),
  mitBereich
    ? { schluessel: 'geschoss', titel: 'Geschoss', wert: (x) => x.geschoss }
    : { schluessel: 'kreis', titel: 'Kreis', wert: (x) => (x.kreis ? KREISNAMEN[x.kreis] ?? x.kreis : null) },
  { schluessel: 'kennwert', titel: 'Kennwert', art: 'zahl', nachkomma: 3, wert: (x) => x.kennwert },
  { schluessel: 'bewertung', titel: 'Bewertung', art: 'status', wert: (x) => x.bewertung, inhalt: (x) => bewertungsmarke(x.bewertung) },
  { schluessel: 'befund', titel: 'Befund', wert: (x) => x.befund ?? null },
];

/**
 * Die Netzteile je Kreis.
 *
 * Nach KREIS und nicht nach Bereich: Ein Lauf „schmutzwasser" trägt Zeilen der
 * Kreise schmutzwasser, kondensat und druckleitung, und sie werden nach
 * verschiedenen Regeln bemessen. Eine Zeile wählt den Kreis; seine Netzteile
 * stehen darunter vollständig (früher ab 200 gekappt).
 */
function Netzteilliste({ daten }: { daten: SanitaerNetzteile }) {
  const [kreis, setKreis] = useState<string | null>(null);
  const zeilen = kreis ? daten.zeilen.filter((x) => (x.kreis ?? 'ohne Kreis') === kreis) : [];

  const kreisSpalten: Spalte<Kreiszeile>[] = [
    { schluessel: 'kreis', titel: 'Kreis', immerSichtbar: true, wert: (k) => KREISNAMEN[k.kreis] ?? k.kreis },
    { schluessel: 'netzteile', titel: 'Netzteile', art: 'zahl', wert: (k) => k.netzteile, fuss: 'summe' },
    { schluessel: 'beanstandet', titel: 'beanstandet', art: 'zahl', wert: (k) => k.beanstandet, fuss: 'summe' },
    { schluessel: 'hinweise', titel: 'Hinweise', art: 'zahl', wert: (k) => k.hinweise, fuss: 'summe' },
    { schluessel: 'kennwertSumme', titel: 'Σ Kennwert', art: 'zahl', nachkomma: 2, wert: (k) => k.kennwertSumme },
    { schluessel: 'kennwertMax', titel: 'größter', art: 'zahl', nachkomma: 3, wert: (k) => k.kennwertMax },
    {
      schluessel: 'bewertung', titel: 'Bewertung', art: 'status',
      wert: (k) => (k.netzteile > 0 ? k.beanstandet / k.netzteile : 0),
      inhalt: (k) => (k.beanstandet === 0
        ? marke('gut', 'ohne Beanstandung')
        : marke('warnung', `${formatiereZahl(k.netzteile > 0 ? (100 * k.beanstandet) / k.netzteile : 0, 0)} % beanstandet`)),
    },
  ];

  return (
    <ContentSection
      titel="Netzteile je Kreis"
      meta={`${formatiereZahl(daten.summe.gesamt, 0)} Netzteile, ${formatiereZahl(daten.summe.beanstandet, 0)} beanstandet`}
    >
      <DataTable
        name="Kreise"
        beschreibung="Eine Zeile zeigt die Netzteile dieses Kreises."
        spalten={kreisSpalten}
        zeilen={daten.jeKreis}
        schluessel={(k) => k.kreis}
        beiZeileKlick={(k) => setKreis(kreis === k.kreis ? null : k.kreis)}
        aktiveZeile={kreis ?? undefined}
        dicht
        suche={false}
        leer={<EmptyState klein titel="Kein Netzteil gerechnet." erklaerung="Netzteile entstehen mit der Sanitärrechnung." />}
      />
      {kreis && (
        <>
          <h3>{KREISNAMEN[kreis] ?? kreis}</h3>
          {daten.gekuerzt && (
            <p className="schwach" data-gekuerzt>
              Der Server liefert höchstens 500 Netzteilzeilen; die Zahlen der Kreise darüber zählen alle. Vollständig
              stehen sie in der Excel-Mappe des Bereichs.
            </p>
          )}
          <DataTable
            name={`Netzteile ${KREISNAMEN[kreis] ?? kreis}`}
            spalten={zeilenSpalten(true)}
            zeilen={zeilen}
            schluessel={(x) => x.id}
            dicht
            ausfuhr={zeilen.length > 20}
            ausfuhrDateiname={`sanitaer-netzteile-${kreis}`}
            leer={<EmptyState klein titel="Keine Zeile in diesem Kreis." />}
          />
        </>
      )}
    </ContentSection>
  );
}

type Anlagenzeile = SanitaerBerechnungsanlagen['gruppen'][number]['zeilen'][number] & { gattungsschluessel: string };

/**
 * Hebeanlagen, Fettabscheider, Zirkulation und die Druckerhöhung - eine Tabelle, je Gattung gruppiert: Die Frage ist
 * eine (welche ANLAGEN folgen aus der Rechnung?), die Anlagen liegen in drei Bereichen.
 */
function Anlagenliste({ daten }: { daten: SanitaerBerechnungsanlagen }) {
  const zeilen: Anlagenzeile[] = daten.gruppen.flatMap((g) => g.zeilen.map((x) => ({ ...x, gattungsschluessel: g.gattung })));
  return (
    <ContentSection
      titel="Anlagen aus der Rechnung"
      meta={`${formatiereZahl(daten.summe.gesamt, 0)} Anlagen, ${formatiereZahl(daten.summe.beanstandet, 0)} beanstandet`}
    >
      <DataTable
        name="Anlagen"
        spalten={zeilenSpalten(false) as unknown as Spalte<Anlagenzeile>[]}
        zeilen={zeilen}
        schluessel={(x) => `${x.gattungsschluessel}|${x.id}`}
        gruppierung={{
          nach: (x) => x.gattungsschluessel,
          titel: (g, liste) => `${GATTUNGSNAMEN[g] ?? g} (${formatiereZahl(liste.length, 0)})`,
        }}
        dicht
        ausfuhr={zeilen.length > 20}
        ausfuhrDateiname="sanitaer-anlagen-aus-der-rechnung"
        leer={<EmptyState klein titel="Keine Anlage aus der Rechnung." erklaerung="Hebeanlagen, Abscheider, Zirkulation und Druckerhöhung entstehen mit der Rechnung." />}
      />
    </ContentSection>
  );
}
